import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStatusName } from '../utils/statusName';

interface Status {
  id: number;
  name: string;
  type?: string;
  order_index: number;
}

interface Props {
  statuses: Status[];
  currentOrder: number;
  // #69: labels of the verified shop are loaded; otherwise the neutral names
  shopLabels: boolean;
}

// #109 (S4, plan section 9): the mockup timeline (.tl). N nodes in a column
// grid (any cardinality, no inline style); the connectors are the ::before
// lines of portal.css, so nothing is measured. The nodes before the current
// status are done, the current one carries aria-current="step"; a current
// status outside the flow highlights no node.
const StatusProgressBar: React.FC<Props> = ({
  statuses,
  currentOrder,
  shopLabels,
}) => {
  const { t } = useTranslation();
  const statusName = useStatusName();

  const sorted = useMemo(
    () => [...statuses].sort((a, b) => a.order_index - b.order_index),
    [statuses]
  );

  const activeIndex = useMemo(
    () => sorted.findIndex(s => s.order_index === currentOrder),
    [sorted, currentOrder]
  );

  return (
    <div
      className="xc-tl"
      role="list"
      aria-label={String(t('tracking.timeline'))}
    >
      {sorted.map((status, i) => {
        const done = activeIndex >= 0 && i < activeIndex;
        const current = i === activeIndex;
        const modifier = done
          ? ' xc-tl__node--done'
          : current
            ? ' xc-tl__node--current'
            : '';
        return (
          <div
            key={status.id}
            className={`xc-tl__node${modifier}`}
            role="listitem"
            aria-current={current ? 'step' : undefined}
          >
            <span className="xc-tl__dot">
              {done && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
              )}
            </span>
            <span className="xc-tl__label">
              {statusName(status, shopLabels)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default StatusProgressBar;
